import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-address-form.css?inline'
import type { ParsedAddress } from './bbcore/types'

// Shape an autocomplete provider returns to fill the suggestion list.
// The widget host owns the actual HTTP call (Google Places / Nominatim);
// this element only owns the debounce + render.
export interface AddressSuggestion {
  label: string
  address: string
  // Google-Places-style place_id; if present, picking the suggestion
  // fires `fetch-place-details` so coords + components are looked up
  // lazily (one Details call per pick). Nominatim-style hosts can return
  // `parsedAddress` inline and skip place_id.
  placeId?: string
  parsedAddress?: ParsedAddress
}

// Search-first delivery-address form — the single source of truth for
// collecting a delivery address, reused across two envelopes:
//   • inline inside the fulfillment affordance (<bb-chat-fulfillment>)
//   • inside the delivery_address recovery modal (<bb-widget>)
//
// The customer starts in the autocomplete search box; picking a result
// (or "Enter address manually" / a sample address) unlocks the
// Street/No./City/Postcode grid. Typing only fetches suggestions — the
// delivery-availability check happens host-side on the set-fulfillment
// the Confirm button emits, never while typing. Confirm is gated on all
// four fields, so a partial address can never be submitted.
//
// Emits the events the host already handles (all composed, so they
// bubble out of either envelope): fetch-address-suggestions,
// fetch-place-details, fetch-sample-address, widget-affordance, and
// set-fulfillment on Confirm.
@customElement('bb-address-form')
export class BbAddressForm extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ type: Boolean }) disabled = false
  // Changing resetKey (e.g. the hint id) clears the form for a fresh
  // address-entry interaction. Hosts pass the current hint's id.
  @property() resetKey = ''
  @property() confirmLabel = 'Confirm delivery address'

  @state() private searchQuery = ''
  // Gates the field grid — disabled until a valid autocomplete pick, the
  // manual fallback, or a sample address.
  @state() private unlocked = false
  @state() private suggestions: AddressSuggestion[] = []
  @state() private fieldNumber = ''
  @state() private fieldStreet = ''
  @state() private fieldCity = ''
  @state() private fieldPostcode = ''
  // Threaded into parsed_address so the validator's country gate passes
  // without a re-geocode. Not user-editable.
  private fieldCountry = ''
  private deliveryParsed: ParsedAddress | undefined
  private suggestSeq = 0
  private suggestTimer: ReturnType<typeof setTimeout> | null = null
  @state() private _busy: 'submit' | 'sample' | null = null

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('resetKey')) {
      this.searchQuery = ''
      this.unlocked = false
      this.suggestions = []
      this.suggestSeq++
      this._busy = null
      this.deliveryParsed = undefined
      this.fieldNumber = ''
      this.fieldStreet = ''
      this.fieldCity = ''
      this.fieldPostcode = ''
      this.fieldCountry = ''
    }
    if (changed.has('disabled')) this._busy = null
  }

  disconnectedCallback() {
    if (this.suggestTimer) clearTimeout(this.suggestTimer)
    super.disconnectedCallback()
  }

  render() {
    const off = this.disabled
    const fieldsOff = off || !this.unlocked
    return html`
      <div class="delivery-form" part="delivery-form">
        ${this.renderSearchField(off)}
        <p class="addr-hint" part="addr-hint">
          Start with your street so we can check delivery availability.
        </p>
        <div class="addr-actions" part="addr-actions">
          <button
            type="button"
            class="addr-link addr-manual"
            part="manual-link"
            ?disabled=${off}
            @click=${() => this.enterManual()}
          >
            Enter address manually
          </button>
          <span class="addr-actions-sep" aria-hidden="true">·</span>
          <button
            type="button"
            class="addr-link sample"
            part="sample-link"
            ?disabled=${off}
            @click=${() => {
              this._busy = 'sample'
              this.emitAffordance('sample-address')
              this.emitSampleAddress()
            }}
          >
            ${this._busy === 'sample'
              ? html`<span
                    class="bb-btn-spinner"
                    part="btn-spinner"
                    aria-hidden="true"
                  ></span>Finding an address…`
              : 'use a sample address'}
          </button>
        </div>
        ${this.unlocked
          ? html`
              ${this.renderAddressGrid(fieldsOff)}
              <button
                class="deliver"
                part="deliver-button"
                ?disabled=${off || !this.fieldsComplete}
                @click=${() => {
                  this._busy = 'submit'
                  this.emitAffordance('delivery-submit', {
                    address: this.composedAddress(),
                  })
                  this.submitDelivery()
                }}
              >
                ${this._busy === 'submit'
                  ? html`<span
                        class="bb-btn-spinner"
                        part="btn-spinner"
                        aria-hidden="true"
                      ></span>Confirming address…`
                  : this.confirmLabel}
              </button>
            `
          : nothing}
      </div>
    `
  }

  // Dedicated autocomplete box. Typing only fetches suggestions; picking
  // a result fills + unlocks the grid.
  private renderSearchField(off: boolean) {
    return html`
      <label class="addr-field addr-search-field">
        <span class="addr-field-label">Search your street or address</span>
        <span class="addr-search-wrap">
          <svg
            class="addr-search-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            part="delivery-input"
            class="addr-search-input"
            .value=${this.searchQuery}
            ?disabled=${off}
            placeholder="Start typing your address…"
            autocomplete="off"
            @input=${(e: Event) => {
              const v = (e.target as HTMLInputElement).value
              this.searchQuery = v
              this.deliveryParsed = undefined
              this.scheduleSuggest(v, v)
            }}
          />
        </span>
        ${this.suggestions.length
          ? html`<ul class="suggestions" part="suggestions" role="listbox">
              ${this.suggestions.map(
                (s) => html`<li role="option" part="suggestion">
                  <button
                    type="button"
                    part="suggestion-button"
                    ?disabled=${off}
                    @click=${() => {
                      this.emitAffordance('address-suggestion', {
                        address: s.address,
                      })
                      this.pickSuggestion(s)
                    }}
                  >
                    ${s.label}
                  </button>
                </li>`,
              )}
            </ul>`
          : nothing}
      </label>
    `
  }

  // Street / No. / City / Postcode — disabled until unlocked. On a
  // `route`-only pick (no house number) the No. field is focused.
  private renderAddressGrid(off: boolean) {
    const field = (
      cls: string,
      label: string,
      value: string,
      key: 'fieldNumber' | 'fieldStreet' | 'fieldCity' | 'fieldPostcode',
      autocomplete: string,
      placeholder = '',
    ) => html`
      <label
        class="addr-field ${cls}"
        data-empty=${off ? 'no' : value.trim() ? 'no' : 'yes'}
      >
        <span class="addr-field-label">${label}</span>
        <input
          type="text"
          .value=${value}
          ?disabled=${off}
          placeholder=${placeholder}
          autocomplete=${autocomplete}
          @input=${(e: Event) => {
            ;(this as unknown as Record<string, string>)[key] = (
              e.target as HTMLInputElement
            ).value
            // A hand-edit invalidates the picked coordinates — the
            // backend re-geocodes the edited address (parsed without
            // coordinates) rather than pinning to the stale place.
            this.deliveryParsed = undefined
            this.requestUpdate()
          }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' && this.fieldsComplete) this.submitDelivery()
          }}
        />
      </label>
    `
    return html`
      <div class="addr-grid" part="addr-grid">
        ${field('addr-street', 'Street', this.fieldStreet, 'fieldStreet', 'address-line1')}
        ${field('addr-no', 'No.', this.fieldNumber, 'fieldNumber', 'address-line2')}
        ${field('addr-city', 'City', this.fieldCity, 'fieldCity', 'address-level2')}
        ${field('addr-zip', 'Postcode', this.fieldPostcode, 'fieldPostcode', 'postal-code')}
      </div>
    `
  }

  // Per-keystroke: debounce 300ms, ask the host for suggestions. Each
  // request carries a monotonic seq so a late response for a stale query
  // is discarded. `streetText` drives the 3-char floor.
  private scheduleSuggest(query: string, streetText: string = query) {
    if (this.suggestTimer) clearTimeout(this.suggestTimer)
    if (streetText.trim().length < 3) {
      this.suggestions = []
      return
    }
    const mySeq = ++this.suggestSeq
    this.suggestTimer = setTimeout(() => {
      this.dispatchEvent(
        new CustomEvent('fetch-address-suggestions', {
          detail: {
            query: query.trim(),
            fill: (suggestions: AddressSuggestion[]) => {
              if (mySeq !== this.suggestSeq) return // stale, ignore
              this.suggestions = suggestions
            },
          },
          bubbles: true,
          composed: true,
        }),
      )
    }, 300)
  }

  private pickSuggestion(s: AddressSuggestion) {
    if (this.disabled) return
    this.searchQuery = s.label
    this.suggestions = []
    this.suggestSeq++
    // Google-Places host: fetch coords + address_components now (one
    // Details call per pick), then unlock + focus No. if it's blank.
    if (s.placeId && !s.parsedAddress) {
      this.dispatchEvent(
        new CustomEvent('fetch-place-details', {
          detail: {
            placeId: s.placeId,
            fill: (
              parsedAddress?: ParsedAddress,
              components?: Array<{ types?: string[]; long_name?: string; short_name?: string }>,
            ) => {
              if (parsedAddress) this.deliveryParsed = parsedAddress
              if (components) this.applyAddressComponents(components)
              this.unlockAfterPick()
            },
          },
          bubbles: true,
          composed: true,
        }),
      )
      return
    }
    // Nominatim host: parsedAddress is inline on the suggestion.
    this.deliveryParsed = s.parsedAddress
    this.applyParsedAddress(s.parsedAddress)
    this.unlockAfterPick()
  }

  // Google `address_components` → the four editable fields.
  private applyAddressComponents(
    components: Array<{ types?: string[]; long_name?: string; short_name?: string }>,
  ) {
    const find = (...keys: string[]) =>
      components.find((c) => (c.types || []).some((t) => keys.includes(t)))
        ?.long_name?.trim() ?? ''
    this.fieldNumber = find('street_number')
    this.fieldStreet = find('route')
    this.fieldCity = find('locality', 'postal_town', 'administrative_area_level_2')
    this.fieldPostcode = find('postal_code')
    this.fieldCountry =
      components.find((c) => (c.types || []).includes('country'))
        ?.short_name?.trim() ?? ''
  }

  private get fieldsComplete(): boolean {
    return (
      !!this.fieldNumber.trim()
      && !!this.fieldStreet.trim()
      && !!this.fieldCity.trim()
      && !!this.fieldPostcode.trim()
    )
  }

  private composedAddress(): string {
    const street = [this.fieldNumber, this.fieldStreet]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ')
    return [street, this.fieldCity.trim(), this.fieldPostcode.trim()]
      .filter(Boolean)
      .join(', ')
  }

  private submitDelivery() {
    if (this.disabled || !this.fieldsComplete) return
    const line1 = `${this.fieldNumber} ${this.fieldStreet}`.trim()
    const formatted = this.composedAddress()
    const parsed: ParsedAddress = {
      line1,
      city: this.fieldCity,
      zipcode: this.fieldPostcode,
      country: this.fieldCountry || undefined,
      formatted_address: formatted,
      ...(this.deliveryParsed?.coordinates
        ? { coordinates: this.deliveryParsed.coordinates }
        : {}),
    }
    this.dispatchEvent(
      new CustomEvent('set-fulfillment', {
        detail: { fulfillmentType: 'delivery', address: formatted, parsedAddress: parsed },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private emitAffordance(kind: string, context?: Record<string, unknown>) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private emitSampleAddress = () => {
    if (this.disabled) return
    this.dispatchEvent(
      new CustomEvent('fetch-sample-address', {
        detail: {
          fill: (_address: string, parsedAddress?: ParsedAddress) => {
            this.deliveryParsed = parsedAddress
            this.applyParsedAddress(parsedAddress)
            this.unlocked = true
            this.suggestions = []
            this.suggestSeq++
            if (this._busy === 'sample') this._busy = null
          },
        },
        bubbles: true,
        composed: true,
      }),
    )
  }

  // Backend flat ParsedAddress → split No. + Street fields.
  private applyParsedAddress(parsed: ParsedAddress | undefined) {
    if (!parsed) return
    const line1 = String(parsed.line1 || '').trim()
    const m = /^(\S+)\s+(.+)$/.exec(line1)
    if (m) {
      this.fieldNumber = m[1]
      this.fieldStreet = m[2]
    } else {
      this.fieldNumber = ''
      this.fieldStreet = line1
    }
    this.fieldCity = String(parsed.city || '')
    this.fieldPostcode = String(parsed.zipcode || '')
    this.fieldCountry = String(parsed.country || '')
  }

  // Manual fallback — unlock with no autocomplete pick.
  private async enterManual() {
    if (this.disabled) return
    this.unlocked = true
    this.suggestions = []
    this.emitAffordance('address-manual')
    await this.updateComplete
    this.renderRoot.querySelector<HTMLInputElement>('.addr-street input')?.focus()
  }

  private async unlockAfterPick() {
    this.unlocked = true
    await this.updateComplete
    if (!this.fieldNumber.trim()) {
      this.renderRoot.querySelector<HTMLInputElement>('.addr-no input')?.focus()
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-address-form': BbAddressForm
  }
}
